import { useCallback, useContext, useEffect, useRef } from 'react';
import {
  AuthenticationResult,
  CacheLookupPolicy,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import { useNavigate } from 'react-router-dom';
import { TokenContext } from '@/Contexts/TokenContext';
import { UsersContext } from '@/Contexts/UsersContext';
import { UserProps } from '@/models/User';
import { trackAuthRedirect } from '@/utils/authTelemetry';
import { logError } from '@/utils/errorHandlerUtil';
import { stringHasValue } from '@/utils/stringUtil';

export function useAuthEvents(pca: PublicClientApplication | null, initialized: boolean) {
  const { updateAccessToken, updateB2cId, updateAuthPca } = useContext(TokenContext);
  const { updateUserCurrent } = useContext(UsersContext);
  const navigate = useNavigate();
  const scope = import.meta.env.VITE_SCOPE as string;
  const viteRedirectUrl = import.meta.env.VITE_REDIRECT_URL as string;

  // Store current callbacks in refs to avoid creating new handleAuthEvent on every dependency change
  const updateB2cIdRef = useRef(updateB2cId);
  const updateAuthPcaRef = useRef(updateAuthPca);
  const updateUserCurrentRef = useRef(updateUserCurrent);
  const navigateRef = useRef(navigate);
  // The account whose sign-in has already been routed. Later token events for the same account are
  // silent refreshes and must not re-run the routing below.
  const routedAccountIdRef = useRef<string | null>(null);

  useEffect(() => {
    updateB2cIdRef.current = updateB2cId;
    updateAuthPcaRef.current = updateAuthPca;
    updateUserCurrentRef.current = updateUserCurrent;
    navigateRef.current = navigate;
  }, [updateB2cId, updateAuthPca, updateUserCurrent, navigate]);

  const handleAuthEvent = useCallback(
    async (event: EventMessage) => {
      if (
        event.payload == null ||
        (event.eventType !== EventType.LOGIN_SUCCESS &&
          event.eventType !== EventType.ACQUIRE_TOKEN_SUCCESS)
      ) {
        return;
      }

      const payload = event.payload as AuthenticationResult;
      const account = payload.account;
      const B2CId = payload.uniqueId;
      const email = account?.username;

      // TODO(bug): useAuthEvents runs inside AuthWrapper, which is *above* UsersProvider (App.tsx),
      // so useContext(UsersContext) here returns the default context and updateUserCurrent is a
      // no-op `() => {}` — the email sync (and the "previous user missing" throw) never runs.
      // Expected: move this sync into UsersProvider (e.g. derive email from authResult there).
      // Update user email
      updateUserCurrentRef.current((prevUser) => {
        if (prevUser == null) {
          throw new Error(
            'Previous user information is missing when trying to update current user after login.'
          );
        }

        if (prevUser.email === email) {
          return prevUser;
        }

        return {
          ...prevUser,
          email: email,
        } satisfies UserProps;
      });

      updateAuthPcaRef.current(pca);
      updateB2cIdRef.current(B2CId);
      pca?.setActiveAccount(account);

      // A later event for an already-routed account is a silent token refresh (useCustomFetch,
      // TokenProvider retry, MSAL renewal): keep the latest token, but don't route or re-acquire.
      const accountId = account?.homeAccountId ?? null;
      if (routedAccountIdRef.current === accountId) {
        if (stringHasValue(payload.accessToken)) {
          updateAccessToken((prevValue: AuthenticationResult | null) =>
            prevValue?.accessToken === payload.accessToken ? prevValue : payload
          );
        }
        return;
      }
      routedAccountIdRef.current = accountId;

      // Handle user state transitions (once per signed-in account)
      if (account?.idTokenClaims?.extension_userIsNew as boolean) {
        navigateRef.current('/signup');
      } else if (account?.idTokenClaims?.isForgotPassword as boolean) {
        const logoutRequest = {
          account: pca?.getAccountByHomeId(B2CId),
          postLogoutRedirectUri: viteRedirectUrl,
        };
        trackAuthRedirect('ForgotPassword', { b2cId: B2CId });
        await pca?.logoutRedirect(logoutRequest);
      } else if (!(account?.idTokenClaims?.extension_userIsValidated as boolean)) {
        navigateRef.current('/inactive');
        return;
      }

      // Acquire access token
      if (account != null) {
        try {
          const response = await pca?.acquireTokenSilent({
            scopes: [scope],
            account,
          });

          if (response != null) {
            updateAccessToken((prevValue: AuthenticationResult | null) => {
              if (prevValue?.accessToken === response.accessToken) {
                return prevValue;
              }
              return response;
            });
          }
        } catch (error) {
          if (error instanceof InteractionRequiredAuthError && pca != null) {
            trackAuthRedirect('InteractionRequired', {
              error,
              b2cId: B2CId,
              trigger: event.eventType,
            });
            await pca.acquireTokenRedirect({
              scopes: [scope],
              account,
            });
            return;
          }

          logError('Error acquiring access token.', error);
        }
      }
    },
    [pca, viteRedirectUrl, scope, updateAccessToken]
  );

  // Register callback only once
  useEffect(() => {
    if (pca == null || !initialized) return;

    const callbackId = pca.addEventCallback(handleAuthEvent);

    return () => {
      if (callbackId != null) {
        pca.removeEventCallback(callbackId);
      }
    };
  }, [pca, initialized, handleAuthEvent]);

  // Acquire token for cached accounts (runs once after initialization)
  useEffect(() => {
    if (pca == null || !initialized) return;

    const accounts = pca.getAllAccounts();
    if (accounts.length === 0) return;

    const account = pca.getActiveAccount() ?? accounts[0];
    pca.setActiveAccount(account);

    pca
      .acquireTokenSilent({
        scopes: [scope],
        account,
      })
      .then((response) => {
        updateAccessToken((prevValue: AuthenticationResult | null) => {
          if (prevValue?.accessToken === response.accessToken) {
            return prevValue;
          }
          return response;
        });
      })
      .catch(async (error) => {
        if (error instanceof InteractionRequiredAuthError) {
          trackAuthRedirect('InteractionRequired', {
            error,
            b2cId: account.localAccountId,
            trigger: 'cachedAccountStartup',
          });
          await pca.acquireTokenRedirect({
            scopes: [scope],
            account,
          });
          return;
        }

        logError('Error acquiring token for cached account', error);
      });
  }, [pca, initialized, scope, updateAccessToken]);

  return {};
}
